import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  RotateCcw,
  Check,
  BookMarked,
  Layers,
  Loader2,
  Filter,
  Play,
  Bot,
  Brain,
} from "lucide-react";
import { ApiService } from "../../../../services/api";
import { openAITeacherForGain } from "../../../../utils/aiTeacher";
import { useModal } from "../../../../hooks/useModal";
import DynamicList from "../../../../components/dynamic/List";
import DeficiencyFilter, { GainStatusFilter } from "./DeficiencyFilter";
import TopicDeficiencyView from "./TopicDeficiencyView";

interface UserGain {
  id: string;
  gainId: string;
  gainText: string;
  startTime?: string;
  endTime?: string;
  isResolved: boolean;
  resolvedAt?: string;
  createdAt: string;
  updatedAt: string;
  lesson: {
    id: string;
    title: string;
  } | null;
  topic: {
    id: string;
    title: string;
  } | null;
  content: {
    id: string;
    title: string;
    type: string;
  } | null;
  exam: {
    id: string;
    status: string;
    endDate?: string;
  } | null;
  question: {
    id: string;
    order: number;
  } | null;
}

interface GainReport {
  summary: {
    totalGains: number;
    resolvedGains: number;
    unresolvedGains: number;
    resolutionRate: number;
  };
  lessonStatistics: Array<{
    lessonId: string;
    lessonTitle: string;
    totalGains: number;
    resolvedGains: number;
    unresolvedGains: number;
    resolutionRate: number;
  }>;
  topicStatistics: Array<{
    topicId: string;
    topicTitle: string;
    lessonId: string;
    lessonTitle: string;
    totalGains: number;
    resolvedGains: number;
    unresolvedGains: number;
    resolutionRate: number;
  }>;
}

interface LessonOption {
  id: string;
  title: string;
}

interface TopicOption {
  id: string;
  title: string;
}

type ActiveTab = "list" | "topics" | "summary";

export default function DeficienciesView() {
  const navigate = useNavigate();
  const { showAlert } = useModal();
  const [activeTab, setActiveTab] = useState<ActiveTab>("topics");

  const [lessons, setLessons] = useState<LessonOption[]>([]);
  const [topics, setTopics] = useState<TopicOption[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<string>("");
  const [selectedTopic, setSelectedTopic] = useState<string>("");
  const [filterResolved, setFilterResolved] = useState<GainStatusFilter>("unresolved");
  const [filterOpen, setFilterOpen] = useState(true);
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [refreshKey, setRefreshKey] = useState(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [report, setReport] = useState<GainReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Session plan — AI öğretmen için kişiselleştirilmiş ders planı
  const [planLoading, setPlanLoading] = useState<string | null>(null); // gainId
  const planPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    loadLessons();
  }, []);

  useEffect(() => {
    if (activeTab === "summary" && !report) {
      loadReport();
    }
  }, [activeTab]);

  // AI Öğretmen bir kazanımı kapattığında liste + özet tazelensin (eski panelin
  // onGainResolved geri çağrısının yerini alır).
  useEffect(() => {
    const onGainResolved = () => {
      setRefreshKey((prev) => prev + 1);
      loadReport();
    };
    window.addEventListener("aiTeacherGainResolved", onGainResolved);
    return () => window.removeEventListener("aiTeacherGainResolved", onGainResolved);
  }, []);

  useEffect(() => {
    try {
      if (typeof window !== "undefined") {
        const isMobile = window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
        if (isMobile) setFilterOpen(false);
      }
    } catch (_) { }
  }, []);

  const loadLessons = async () => {
    try {
      const lessonsRes = await ApiService.getLessons(1, 100);
      setLessons(lessonsRes?.data || []);
    } catch (error) {
      console.error("Error loading lessons:", error);
    }
  };

  const loadTopics = async (lessonId: string) => {
    if (!lessonId) {
      setTopics([]);
      return;
    }
    try {
      setTopicsLoading(true);
      const sectionsRes = await ApiService.getLessonSections(lessonId);
      const sectionsList = sectionsRes?.data || sectionsRes || [];
      setTopics(Array.isArray(sectionsList) ? sectionsList.map((s: any) => ({ id: s.id, title: s.title })) : []);
    } catch (error) {
      console.error("Error loading topics:", error);
      setTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLesson) {
      loadTopics(selectedLesson);
      setSelectedTopic("");
    } else {
      setTopics([]);
      setSelectedTopic("");
    }
  }, [selectedLesson]);

  const loadReport = async () => {
    try {
      setReportLoading(true);
      const reportRes = await ApiService.getGainReports();
      setReport(reportRes || null);
    } catch (error) {
      console.error("Error loading report:", error);
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  const openAITutorWithPlan = async (gain: UserGain) => {
    const baseContext = {
      gainId: gain.id,
      gainText: gain.gainText,
      contentId: gain.content?.id || "",
      contentTitle: gain.content?.title || "",
      topicId: gain.topic?.id || "",
      topicTitle: gain.topic?.title || "",
      lessonId: gain.lesson?.id || "",
      lessonTitle: gain.lesson?.title || "",
      startTime: gain.startTime || "",
      endTime: gain.endTime || "",
      pdfUrl: (gain.content as any)?.media?.url || "",
      source: "deficiency",
    };

    setPlanLoading(gain.id);
    try {
      // TEK MERKEZ: açılış utils/aiTeacher üzerinden. Planı sunucu kurar (teach/start) —
      // föydeki tüm açık eksikler, transkript aralığı, kazanıma özel PDF sayfası,
      // havuz soruları ve oturum kabuğu birlikte gelir; ugain_ çevirisini sunucu yapar.
      await openAITeacherForGain(gain.id, baseContext, {
        onCompleted: (message) => void showAlert({ title: "Eksik kalmadı", message, type: "success" }),
      });
    } finally {
      setPlanLoading(null);
    }
  };

  const handleResolve = async (gainId: string) => {
    try {
      setActionLoading(gainId);
      await ApiService.resolveGain(gainId);
      setRefreshKey((prev) => prev + 1);
      await loadReport();
      await showAlert({
        title: "Başarılı",
        message: "Eksik tamamlandı olarak işaretlendi.",
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "Eksik tamamlanırken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnresolve = async (gainId: string) => {
    try {
      setActionLoading(gainId);
      await ApiService.unresolveGain(gainId);
      setRefreshKey((prev) => prev + 1);
      await loadReport();
      await showAlert({
        title: "Başarılı",
        message: "Eksik tekrar açıldı.",
        type: "success",
      });
    } catch (error: any) {
      await showAlert({
        title: "Hata",
        message: error.message || "İşlem sırasında bir hata oluştu.",
        type: "error",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getProgressColor = (rate: number) => {
    if (rate >= 80) return "bg-green-500";
    if (rate >= 50) return "bg-yellow-500";
    if (rate >= 25) return "bg-orange-500";
    return "bg-red-500";
  };

  const getProgressBgColor = (rate: number) => {
    if (rate >= 80) return "bg-green-100";
    if (rate >= 50) return "bg-yellow-100";
    if (rate >= 25) return "bg-orange-100";
    return "bg-red-100";
  };

  const clearFilters = () => {
    setFilterResolved("unresolved");
    setSelectedLesson("");
    setSelectedTopic("");
    setRefreshKey((prev) => prev + 1);
  };

  const getApiPath = () => {
    const params = new URLSearchParams();
    if (selectedLesson) params.append("lessonId", selectedLesson);
    if (selectedTopic) params.append("topicId", selectedTopic);
    if (filterResolved === "resolved") params.append("isResolved", "true");
    if (filterResolved === "unresolved") params.append("isResolved", "false");
    const qs = params.toString();
    return `gains${qs ? `?${qs}` : ""}`;
  };

  const renderGainItem = useCallback(
    (gain: UserGain, viewMode?: string) => {
      const isLoading = actionLoading === gain.id;
      const isGrid = viewMode === "grid";

      return (
        <div
          key={gain.id}
          className={`bg-white rounded-xl border-2 shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden ${gain.isResolved ? "border-green-300 bg-green-50/50" : "border-gray-200 hover:border-gray-300"
            } ${isGrid ? "p-3 sm:p-4 h-full" : "p-3 sm:p-4 mb-3"}`}>
          <div className="h-full flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {gain.isResolved ? (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                ) : (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                  </div>
                )}
                <span
                  className={`text-xs sm:text-sm font-semibold px-2 sm:px-3 py-1 rounded-full whitespace-nowrap ${gain.isResolved ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                    }`}>
                  {gain.isResolved ? "Tamamlandı" : "Bekliyor"}
                </span>
              </div>
              <div className="text-xs text-gray-500 font-medium flex items-center gap-1.5 ml-9 sm:ml-10">
                <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                {formatDate(gain.createdAt)}
              </div>
            </div>

            <p className="text-gray-900 font-semibold text-sm sm:text-base leading-relaxed break-words">{gain.gainText}</p>

            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              {gain.lesson && (
                <button
                  onClick={() => navigate(`/lessons/detail/${gain.lesson!.id}`)}
                  className="inline-flex items-center gap-1 sm:gap-1.5 bg-blue-100 text-blue-800 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm max-w-full hover:bg-blue-200 transition-colors cursor-pointer">
                  <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{gain.lesson.title}</span>
                </button>
              )}
              {gain.topic && gain.topic.title && (
                <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-purple-100 text-purple-800 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm max-w-full">
                  <Layers className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{gain.topic.title}</span>
                </span>
              )}

              {gain.content && (
                <button
                  onClick={() => gain.lesson && navigate(`/lessons/play/${gain.lesson.id}?contentId=${gain.content!.id}`)}
                  disabled={!gain.lesson}
                  className={`inline-flex items-center gap-1 sm:gap-1.5 bg-gray-100 text-gray-700 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm max-w-full ${gain.lesson ? "hover:bg-gray-200 transition-colors cursor-pointer" : "cursor-default"
                    }`}>
                  <BookMarked className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span className="truncate">{gain.content.title}</span>
                </button>
              )}
              {gain.question && (
                <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-red-100 text-red-800 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm whitespace-nowrap">
                  <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{gain.question.order}. Soru</span>
                </span>
              )}

              {gain.lesson && (gain.startTime || gain.endTime) && (
                <span className="inline-flex items-center gap-1 sm:gap-1.5 bg-orange-100 text-orange-800 font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm whitespace-nowrap">
                  <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>
                    {gain.startTime && gain.endTime
                      ? `${gain.startTime} - ${gain.endTime}`
                      : gain.startTime
                        ? `${gain.startTime}`
                        : `${gain.endTime}`}
                  </span>
                </span>
              )}
            </div>

            <div className={`mt-auto flex justify-center pt-3 border-t border-gray-200 ${isGrid ? "flex-col gap-2" : "gap-2"}`}>
              {/* "İncele & Çöz" kaldırıldı (9 Ağu 2026): eski DeficiencyTutorPanel'e gidiyordu —
                  ayrı motor, soru üretimi açık, farklı kapama eşiği, telemetri yok.
                  Tek giriş "AI Öğretmen Destek". */}
              <button
                onClick={() => openAITutorWithPlan(gain)}
                disabled={planLoading === gain.id}
                className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed ${isGrid ? "w-full" : ""}`}>
                {planLoading === gain.id ? (
                  <>
                    <Brain className="w-4 h-4 animate-pulse" />
                    Ders planı hazırlanıyor...
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4" />
                    AI Öğretmen Destek
                  </>
                )}
              </button>
              {gain.isResolved ? (
                <button
                  onClick={() => handleUnresolve(gain.id)}
                  disabled={isLoading}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 ${isGrid ? "w-full" : ""
                    }`}>
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  Geri Al
                </button>
              ) : (
                <button
                  onClick={() => handleResolve(gain.id)}
                  disabled={isLoading}
                  className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${isGrid ? "w-full" : ""
                    }`}>
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Tamamlandı İşaretle
                </button>
              )}
            </div>
          </div>
        </div>
      );
    },
    [actionLoading]
  );

  const renderFilterActions = () => (
    <div className="flex flex-row flex-wrap gap-2">
      <button
        onClick={() => setFilterOpen((prev) => !prev)}
        className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 ${filterOpen
          ? "bg-blue-600 text-white hover:bg-blue-700 border-blue-600"
          : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300"
          }`}
        title="Filtrele"
        aria-pressed={filterOpen}>
        <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span>Filtrele</span>
      </button>
    </div>
  );

  const renderFilterArea = () => (
    <DeficiencyFilter
      selectedStatus={filterResolved}
      onStatusChange={(status) => {
        setFilterResolved(status);
        setRefreshKey((prev) => prev + 1);
      }}
      selectedLesson={selectedLesson}
      onLessonChange={(lessonId) => {
        setSelectedLesson(lessonId);
        setRefreshKey((prev) => prev + 1);
      }}
      lessons={lessons}
      selectedTopic={selectedTopic}
      onTopicChange={(topicId) => {
        setSelectedTopic(topicId);
        setRefreshKey((prev) => prev + 1);
      }}
      topics={topics}
      topicsLoading={topicsLoading}
      onClearFilters={clearFilters}
    />
  );

  const renderSummaryTab = () => {
    if (reportLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mb-4" />
          <p className="text-sm text-gray-400">Özet yükleniyor...</p>
        </div>
      );
    }

    if (!report) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
          <TrendingUp className="w-16 h-16 mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">Rapor Bulunamadı</p>
          <p className="text-sm text-gray-400">Henüz kazanım veriniz bulunmuyor</p>
        </div>
      );
    }

    return (
      <div className="space-y-6 p-4 sm:p-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs sm:text-sm font-medium">Toplam Eksik</span>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{report.summary.totalGains}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs sm:text-sm font-medium">Bekleyen</span>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-amber-600">{report.summary.unresolvedGains}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs sm:text-sm font-medium">Tamamlanan</span>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-green-600">{report.summary.resolvedGains}</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-gray-500 text-xs sm:text-sm font-medium">İlerleme</span>
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-purple-600">%{report.summary.resolutionRate}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Genel İlerleme</h3>
          <div className="relative h-5 sm:h-6 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${getProgressColor(report.summary.resolutionRate)} transition-all duration-500`}
              style={{ width: `${report.summary.resolutionRate}%` }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs sm:text-sm font-semibold text-gray-800">
                {report.summary.resolvedGains} / {report.summary.totalGains} Tamamlandı
              </span>
            </div>
          </div>
        </div>

        {report.lessonStatistics.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Derslere Göre Dağılım</h3>
            <div className="space-y-4">
              {report.lessonStatistics
                .sort((a, b) => b.unresolvedGains - a.unresolvedGains)
                .map((stat) => (
                  <div key={stat.lessonId} className="space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2">
                      <span className="text-sm font-medium text-gray-700 truncate">{stat.lessonTitle}</span>
                      <div className="flex items-center gap-2 sm:gap-3 text-xs flex-shrink-0">
                        <span className="text-amber-600">{stat.unresolvedGains} Bekliyor</span>
                        <span className="text-green-600">{stat.resolvedGains} Tamamlandı</span>
                        <span className="font-semibold text-gray-900">%{stat.resolutionRate}</span>
                      </div>
                    </div>
                    <div className={`relative h-2 ${getProgressBgColor(stat.resolutionRate)} rounded-full overflow-hidden`}>
                      <div
                        className={`absolute inset-y-0 left-0 ${getProgressColor(stat.resolutionRate)} transition-all duration-500`}
                        style={{ width: `${stat.resolutionRate}%` }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {report.topicStatistics.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Konulara Göre Dağılım</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {report.topicStatistics
                .sort((a, b) => b.unresolvedGains - a.unresolvedGains)
                .slice(0, 10)
                .map((stat) => (
                  <div
                    key={stat.topicId}
                    className={`p-3 sm:p-4 rounded-lg border ${stat.unresolvedGains > 0 ? "border-amber-200 bg-amber-50/50" : "border-green-200 bg-green-50/50"
                      }`}>
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{stat.topicTitle}</p>
                        <p className="text-xs text-gray-500 truncate">{stat.lessonTitle}</p>
                      </div>
                      <span
                        className={`text-xs font-bold px-2 py-1 rounded-full flex-shrink-0 ${stat.resolutionRate >= 80
                          ? "bg-green-100 text-green-700"
                          : stat.resolutionRate >= 50
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                          }`}>
                        %{stat.resolutionRate}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-amber-600">{stat.unresolvedGains} Bekliyor</span>
                      <span className="text-gray-300">•</span>
                      <span className="text-green-600">{stat.resolvedGains} Tamamlandı</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full bg-gray-50 flex flex-col">
      <div className="flex-1 min-h-0 px-3 sm:px-4">
        <div className="h-full bg-gray-50 relative rounded-lg shadow-sm flex flex-col mr-1">
          <div className="px-4 sm:px-6 py-4 sm:py-6 w-full flex-1 flex flex-col min-h-0">
            <div className="mb-4 sm:mb-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900">Eksikler</span>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 text-sm text-gray-600 overflow-x-auto scrollbar-hide w-full sm:w-auto -mx-2 px-2 whitespace-nowrap">
                  <button
                    onClick={() => setActiveTab("topics")}
                    className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "topics" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                      }`}>
                    Konu Bazlı
                  </button>
                  <button
                    onClick={() => setActiveTab("list")}
                    className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "list" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                      }`}>
                    Kazanım Bazlı
                  </button>
                  <button
                    onClick={() => setActiveTab("summary")}
                    className={`pb-3 transition-all duration-200 flex-shrink-0 ${activeTab === "summary" ? "text-blue-600 font-medium border-b-2 border-blue-500" : "hover:text-gray-900"
                      }`}>
                    Özet
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden">
              {activeTab === "list" && (
                <div className="deficiencies-dynamic-list h-full">
                  <DynamicList
                    type="grid"
                    header={false}
                    title="Eksikler"
                    path={getApiPath()}
                    isSearch={false}
                    isPagination={true}
                    showToggle={true}
                    refreshKey={refreshKey}
                    actions={renderFilterActions()}
                    leftArea={filterOpen ? renderFilterArea : undefined}
                    item={renderGainItem}
                    externalViewMode={viewMode}
                    onViewModeChange={setViewMode}
                    emptyMessage="Harika! Eksik kazanımınız yok. Tüm kazanımlarınızı tamamladınız veya henüz sınava girmediniz."
                    errorMessage="Eksikler yüklenirken bir hata oluştu"
                  />
                </div>
              )}
              {activeTab === "topics" && <TopicDeficiencyView />}
              {activeTab === "summary" && (
                <div className="h-full overflow-y-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
                  {renderSummaryTab()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
